import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { PositionStaffingCommandServerPort } from '../commands/PositionStaffingCommand';
import type { OrgModelProjection, OrgModelRelationship } from '../models/OrgModelProjection';
import type { PositionStaffingIntent, PositionStaffingResult } from '../models/PositionStaffingIntent';
import { invalidateOrgPlanningQueries } from './invalidateOrgPlanningQueries';
import { usePositionStaffingOptimisticStore } from '../stores/positionStaffingOptimisticStore';
import { recordPositionStaffingTelemetry } from '../services/PositionStaffingTelemetry';
import {
  PositionCommandSettlementError,
  isPositionCommandConflict,
  postPositionIntentWithReconciliation,
  settlePositionCommand,
} from './positionCommandSettlement';
import { liveOrgModelQueryKey } from './orgModelQueryKeys';
import { enqueueOrgPlanningTransition } from './orgPlanningCommandSequencer';
import { successionOptimisticContext } from '../stores/successionOptimisticSlateStore';
import { captureOrgPlanningReviewScope, isOrgPlanningReviewScopeCurrent, seedOrgPlanningReview } from './useOrgPlanningReviewScope';
import { createOrgPlanningDraftCommandPort, type DraftHistoryAction } from './createOrgPlanningDraftCommandPort';

export type PositionStaffingOperationState = 'idle' | 'pending' | 'indeterminate' | 'conflict' | 'failed';

export interface PositionStaffingOperationNotice {
  readonly state: PositionStaffingOperationState;
  readonly message: string;
}

/** Server port factory used by PositionStaffingCommand. Live writes are serialized per mounted chart. */
export function usePositionStaffingCommands(input: {
  folderId: string | null | undefined;
  diagramId?: string;
  /** Routes the semantic intent to the canonical operational-chart draft PEP. */
  operational?: boolean;
  implicitChartEdit?: boolean;
  onNotice?: (notice: PositionStaffingOperationNotice) => void;
  onScenarioChangeSet?: (changeSetId: string, scenarioId?: string | null) => void;
}) {
  const { folderId, onNotice, onScenarioChangeSet, operational = false, implicitChartEdit = false } = input;
  const diagramId = input.diagramId ?? 'test';
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const currentModel = useCallback((): OrgModelProjection | undefined => {
    return queryClient.getQueryData<OrgModelProjection>(
      liveOrgModelQueryKey(folderId),
    );
  }, [folderId, queryClient]);

  const refreshAndReconcile = useCallback(async (result?: PositionStaffingResult) => {
    if (!folderId) return;
    if (result?.outcome === 'staged') {
      if (result.changeSetId) onScenarioChangeSet?.(result.changeSetId, result.scenarioId);
      void queryClient.invalidateQueries({
        queryKey: ['atlas', 'organization-plans', folderId, diagramId],
        refetchType: 'active',
      });
      void queryClient.invalidateQueries({
        queryKey: ['atlas', 'organization-plan-review', folderId, diagramId],
        refetchType: 'none',
      });
      onNotice?.({ state: 'idle', message: operational ? 'Operational change ready to review.' : 'Staffing change added to scenario.' });
      return;
    }
    await settlePositionCommand(
      invalidateOrgPlanningQueries(queryClient, folderId),
      'refresh_timeout',
    );
    if (!result || result.outcome !== 'applied') return;
    const model = currentModel();
    if (!model || model.graphVersion < result.graphVersion || !resultMatchesModel(result, model.relationships)) {
      onNotice?.({
        state: 'conflict',
        message: 'The chart changed while staffing was saved. The latest organization state is shown.',
      });
      usePositionStaffingOptimisticStore.getState().setOperation(result ? {
        ...optimisticOperationForResult(result), status: 'conflict',
      } : null);
      return;
    }
    usePositionStaffingOptimisticStore.getState().setOperation(null);
    onNotice?.({ state: 'idle', message: 'Staffing change saved.' });
  }, [currentModel, diagramId, folderId, onNotice, onScenarioChangeSet, operational, queryClient]);

  const request = useCallback(async (
    intent: PositionStaffingIntent,
    signal?: AbortSignal,
    publishStaged = true,
  ): Promise<PositionStaffingResult> => {
    if (!folderId) throw new Error('Position staffing requires a folder-backed chart.');
    const reviewScope = captureOrgPlanningReviewScope(folderId, diagramId);
    onNotice?.({
      state: 'pending',
      message: operational ? 'Preparing organization change…' : intent.mode === 'scenario' ? 'Adding to scenario…' : 'Saving staffing change…',
    });
    // Operational commands are still authored from the Live chart. Paint the
    // same value-free seat affordance immediately while the server prepares
    // the authoritative review; protected facts and confirmation remain
    // server-owned.
    if (intent.mode === 'live') {
      const model = currentModel();
      usePositionStaffingOptimisticStore.getState().setOperation(optimisticOperationForIntent(intent, model));
    }
    let response: Response;
    try {
      response = await postPositionIntentWithReconciliation(
        () => {
          if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw new DOMException('Planning access changed.', 'AbortError');
          return authenticatedFetch(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/${operational ? 'operational-staffing-intents' : implicitChartEdit ? 'implicit-chart-staffing-intents' : 'staffing-intents'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(intent),
          signal,
          });
        },
        () => onNotice?.({ state: 'pending', message: 'The request is still settling. Confirming the same staffing intent…' }),
      );
    } catch (error) {
      if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw error;
      settlePositionStaffingFailure(false);
      const indeterminate = error instanceof PositionCommandSettlementError
        && error.code === 'request_timeout';
      if (indeterminate) {
        await invalidateOrgPlanningQueries(queryClient, folderId).catch(() => undefined);
      }
      onNotice?.({
        state: indeterminate ? 'indeterminate' : 'failed',
        message: indeterminate
          ? 'The server outcome is not confirmed. Refresh the chart before making another staffing change.'
          : 'The staffing change could not be saved. Check your connection and try again.',
      });
      recordPositionStaffingTelemetry({ action: intent.kind, mode: intent.mode, outcome: 'blocked' });
      throw error;
    }
    if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw new DOMException('Planning access changed.', 'AbortError');
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as { error?: string };
      if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw new DOMException('Planning access changed.', 'AbortError');
      const conflict = positionStaffingFailureIsConflict(response.status, payload.error);
      if (conflict) {
        await invalidateOrgPlanningQueries(queryClient, folderId).catch(() => undefined);
      }
      onNotice?.({
        state: conflict ? 'conflict' : 'failed',
        message: staffingErrorMessage(payload.error, conflict),
      });
      recordPositionStaffingTelemetry({ action: intent.kind, mode: intent.mode, outcome: 'blocked' });
      settlePositionStaffingFailure(conflict);
      throw new PositionCommandSettlementError('http_error', response.status, payload.error);
    }
    let result: PositionStaffingResult;
    try {
      result = await response.json() as PositionStaffingResult;
      if (result.intentId !== intent.intentId || !Number.isFinite(result.graphVersion)
        || !['applied', 'staged', 'noop'].includes(result.outcome)) throw new Error('invalid response');
    } catch {
      if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw new DOMException('Planning access changed.', 'AbortError');
      settlePositionStaffingFailure(false);
      onNotice?.({ state: 'failed', message: 'The server response was incomplete. Refresh the chart before retrying.' });
      throw new PositionCommandSettlementError('http_error', 502, 'malformed_response');
    }
    if (!isOrgPlanningReviewScopeCurrent(reviewScope)) throw new DOMException('Planning access changed.', 'AbortError');
    recordPositionStaffingTelemetry({ action: intent.kind, mode: intent.mode, outcome: result.outcome });
    if (!publishStaged && result.outcome === 'noop') return result;
    if (result.outcome === 'staged') {
      if (!publishStaged) return result;
      if (operational && result.changeSetId && result.review) {
        seedOrgPlanningReview(queryClient, reviewScope, result.changeSetId, result.review);
      }
      onNotice?.({ state: 'idle', message: operational ? 'Operational change ready to review.' : 'Staffing change added to scenario.' });
      void refreshAndReconcile(result);
      return result;
    }
    try {
      await refreshAndReconcile(result);
    } catch (error) {
      usePositionStaffingOptimisticStore.getState().setOperation(null);
      onNotice?.({ state: 'failed', message: 'The change was accepted, but the chart did not refresh. Refresh the chart to confirm it.' });
      throw error;
    }
    return result;
  }, [authenticatedFetch, currentModel, diagramId, folderId, implicitChartEdit, onNotice, operational, queryClient, refreshAndReconcile]);

  const transition = useCallback(async (
    changeSetId: string,
    action: 'discard' | 'restore' | 'revert' | DraftHistoryAction,
    signal?: AbortSignal,
    publish = true,
    restoreChangeSetId?: string,
  ) => {
    if (!folderId) return null;
    onNotice?.({ state: 'pending', message: `${capitalizeTransition(action)} staffing change…` });
    try {
      const response = await settlePositionCommand(authenticatedFetch(
        `/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${changeSetId}/${action}`,
        { method: 'POST', signal, ...(restoreChangeSetId ? {
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restoreChangeSetId }),
        } : {}) },
      ), 'request_timeout');
      const transitioned = await response.json().catch(() => null) as { id?: string; error?: string } | null;
      if (!response.ok) throw new PositionCommandSettlementError('http_error', response.status, transitioned?.error);
      if (!transitioned?.id) throw new PositionCommandSettlementError('http_error', 502, 'malformed_response');
      if (publish) await refreshAndReconcile();
      onNotice?.({ state: 'idle', message: 'Staffing history updated.' });
      return transitioned.id;
    } catch (error) {
      onNotice?.({
        state: positionStaffingFailureIsConflict(
          error instanceof PositionCommandSettlementError ? error.status ?? 0 : 0,
          error instanceof PositionCommandSettlementError ? error.serverCode : undefined,
        ) ? 'conflict' : 'failed',
        message: error instanceof PositionCommandSettlementError && error.status === 409
          ? 'The organization changed. Review the latest chart and try again.'
          : 'The staffing history change did not settle. Refresh before retrying.',
      });
      throw error;
    }
  }, [authenticatedFetch, diagramId, folderId, onNotice, refreshAndReconcile]);

  const createServerPort = useCallback((initialIntent: PositionStaffingIntent): PositionStaffingCommandServerPort => {
    if (initialIntent.mode === 'scenario' && !operational) {
      if (!folderId) throw new Error('Position staffing requires a folder-backed chart.');
      return createOrgPlanningDraftCommandPort({
        folderId, diagramId, initial: initialIntent, queryClient,
        submit: (intent, signal) => request(intent, signal, false),
        transition: (head, action, signal, restoreId) => transition(head, action, signal, false, restoreId),
        onNoop: () => onNotice?.({ state: 'idle', message: 'No staffing change was needed.' }),
        onError: (error) => {
          const uncertain = error instanceof PositionCommandSettlementError
            && (error.code === 'request_timeout' || error.serverCode === 'malformed_response');
          onNotice?.({ state: uncertain ? 'indeterminate' : isPositionCommandConflict(error) ? 'conflict' : 'failed',
            message: uncertain ? 'The save outcome is not confirmed. Review the saved draft before retrying.'
              : isPositionCommandConflict(error) ? 'The selected plan or its head changed. Review it before retrying this edit.'
                : 'The draft change could not be saved.',
          });
        },
        onAccepted: (head, scenarioId) => {
          onScenarioChangeSet?.(head, scenarioId);
          onNotice?.({ state: 'idle', message: 'Staffing change saved to draft.' });
        },
      });
    }
    const context = successionOptimisticContext(folderId ?? '', diagramId);
    let result: PositionStaffingResult | undefined;
    let intent = initialIntent;
    let inverseChangeSetId: string | null = null;
    const adoptStagedHead = () => {
      if (result?.outcome !== 'staged' || !result.changeSetId) return;
      intent = {
        ...intent,
        scenarioChangeSetId: result.changeSetId,
        scenarioId: result.scenarioId ?? intent.scenarioId,
        expectedHeadChangeSetId: result.changeSetId,
      };
    };
    const serialized = (operation: (signal: AbortSignal) => Promise<void>): Promise<void> =>
      enqueueOrgPlanningTransition(context, operation, queryClient);
    return {
      execute: () => serialized(async (signal) => {
        result = await request(intent, signal);
        adoptStagedHead();
      }),
      undo: () => serialized(async (signal) => {
        if (!result?.changeSetId || result.outcome === 'noop') return;
        if (result.outcome === 'staged') {
          throw new Error('Use Changes to return an operational change to editing.');
        }
        inverseChangeSetId = inverseChangeSetId ?? await transition(result.changeSetId, 'revert', signal);
      }),
      redo: () => serialized(async (signal) => {
        if (result?.outcome === 'applied' && inverseChangeSetId) {
          inverseChangeSetId = null;
        }
        const model = currentModel();
        intent = {
          ...intent,
          intentId: crypto.randomUUID(),
          expectedGraphVersion: model?.graphVersion ?? result?.graphVersion ?? intent.expectedGraphVersion,
          scenarioChangeSetId: intent.mode === 'scenario' ? intent.scenarioChangeSetId : undefined,
        };
        result = await request(intent, signal);
        adoptStagedHead();
      }),
    };
  }, [currentModel, diagramId, folderId, onNotice, onScenarioChangeSet, operational, queryClient, request, transition]);

  return { createServerPort };
}

/** Keeps true concurrency conflicts reviewable and rolls back every other failure. */
export function settlePositionStaffingFailure(conflict: boolean): void {
  const store = usePositionStaffingOptimisticStore.getState();
  store.setOperation(conflict && store.operation ? { ...store.operation, status: 'conflict' } : null);
}

export function positionStaffingFailureIsConflict(status: number, code?: string): boolean {
  if (status !== 409) return false;
  return !code || [
    'graph_version_conflict',
    'source_version_conflict',
    'authorization_policy_version_conflict',
    'scenario_revision_conflict',
  ].includes(code);
}


function resultMatchesModel(result: PositionStaffingResult, relationships: readonly OrgModelRelationship[]): boolean {
  const expected = result.expectedRelationship;
  const expectedAbsent = result.expectedAbsentAssignmentId;
  const hasExpected = !expected || relationships.some((relationship) => relationship.predicate === 'occupies'
    && (relationship.id === expected.assignmentId || relationship.externalId === expected.assignmentId)
    && relationship.subjectRef === expected.personId
    && relationship.objectRef === expected.positionId
    && relationship.qualifiers.assignmentType === expected.assignmentType);
  const hasClosedSource = !expectedAbsent || !relationships.some((relationship) => relationship.predicate === 'occupies'
    && (relationship.id === expectedAbsent || relationship.externalId === expectedAbsent));
  return hasExpected && hasClosedSource;
}

function staffingErrorMessage(code: string | undefined, conflict: boolean): string {
  if (code === 'target_primary_occupied') return 'That position already has a primary occupant.';
  if (code === 'person_primary_occupied') return 'That person already has a primary position.';
  if (code === 'successor_is_incumbent') return 'That person already holds this position.';
  if (code === 'projection_incomplete') return 'Staffing is unavailable because the permitted organization view is incomplete.';
  if (conflict) return 'The organization changed. Review the latest chart and try again.';
  return 'The staffing change could not be saved.';
}

function capitalizeTransition(action: 'discard' | 'restore' | 'revert' | DraftHistoryAction): string {
  return action.charAt(0).toUpperCase() + action.slice(1);
}

function optimisticOperationForIntent(intent: PositionStaffingIntent, model: OrgModelProjection | undefined) {
  // A transition keys on the occupancy it vacates, and that seat is also where
  // the successor lands — so it is both the source and the target of the
  // pending affordance, unlike a move.
  const sourceAssignmentId = intent.kind === 'transition'
    ? intent.vacatingAssignmentId
    : 'sourceAssignmentId' in intent ? intent.sourceAssignmentId : null;
  const source = sourceAssignmentId
    ? model?.relationships.find((relationship) => relationship.predicate === 'occupies'
      && (relationship.id === sourceAssignmentId || relationship.externalId === sourceAssignmentId))
    : undefined;
  return {
    intentId: intent.intentId,
    sourcePositionId: source?.objectRef ?? null,
    targetPositionId: intent.kind === 'transition'
      ? source?.objectRef ?? null
      : 'targetPositionId' in intent ? intent.targetPositionId : null,
    status: 'pending' as const,
  };
}

function optimisticOperationForResult(result: PositionStaffingResult) {
  return {
    intentId: result.intentId,
    sourcePositionId: null,
    targetPositionId: result.expectedRelationship?.positionId ?? null,
  };
}
